import { ApolloError } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react/hooks/useLazyQuery'
import { summarizeApolloError } from '@deltek/specification-client/core/Utilities/apolloUtils'
import React from 'react'

import { BestPracticeDataResponse } from '@deltek/specification-client/core/Models/bestPractice'
import { BestPracticeQuery } from 'api/graphQL/queries/bestPractices.queries'
import { useHoveredItemActions } from '../HoveredItemStore'

export interface BestPracticesApiProps {
    /** Id of the current project loaded in workspace */
    projectId?: string
}

/**
 * A custom hook that provides functionality to fetch and manage best practice element data.
 *
 * @template T - The type of the props that extend `BestPracticesApiProps`.
 * @param {T} props - The properties required for the API, including the project ID.
 * @returns {Object} An object containing the `fetchBestPracticeElementData` function.
 *
 * @typedef {Object} BestPracticesApiProps
 * @property {string} projectId - The ID of the project for which to fetch best practice data.
 *
 * @typedef {Object} BestPracticeDataResponse
 * @property {Array} data - The data returned from the best practice query.
 *
 * @function fetchBestPracticeElementData
 * @description Fetches the best practice element data for a given parent ID.
 * This function is memoized using `React.useCallback` to prevent unnecessary re-renders.
 * It sets the loading state to true before making the API call to fetch the data.
 * @param {string} dtvParentId - The ID of the parent element for which to fetch the best practice data.
 * @returns {Promise<void>} A promise that resolves when the data fetching is complete.
 */
export const useBestPracticesQuery = <T extends BestPracticesApiProps>({
    projectId = '',
}: T) => {
    const {
        setBestPracticeElementData,
        setIsBestPracticeElementDataLoading,
    } = useHoveredItemActions()
    const handleFetchError = (error: ApolloError) => {
        setIsBestPracticeElementDataLoading(false)

        const summarizedError = summarizeApolloError(
            error,
            'WorkspaceEditor-getBestPracticeQuery'
        )
        console.error(summarizedError)
    }

    const onQueryFetchSuccess = (response: BestPracticeDataResponse) => {
        setBestPracticeElementData(response)
        setIsBestPracticeElementDataLoading(false)
    }

    const [getBestPracticeElementData] = useLazyQuery<BestPracticeDataResponse>(
        BestPracticeQuery,
        {
            fetchPolicy: 'no-cache',
            onCompleted: onQueryFetchSuccess,
            onError: handleFetchError,
        }
    )

    /**
     * Fetches the best practice element data for a given parent ID.
     *
     * This function is memoized using `React.useCallback` to prevent unnecessary re-renders.
     * It sets the loading state to true before making the API call to fetch the data.
     *
     * @param {string} dtvParentId - The ID of the parent element for which to fetch the best practice data.
     * @returns {Promise<void>} A promise that resolves when the data fetching is complete.
     */
    const fetchBestPracticeElementData = React.useCallback(
        async (dtvParentId: string): Promise<void> => {
            const queryVariables = {
                parentId: dtvParentId,
                projectId,
            }
            setIsBestPracticeElementDataLoading(true)
            return getBestPracticeElementData({
                variables: queryVariables,
            })
        },
        [projectId]
    )

    return { fetchBestPracticeElementData }
}
